import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Text, Card } from '@teals/ui-web';
import { useAuth } from '../../hooks/useAuth';
import {
  useGetInvitePreviewQuery,
  useAcceptInvitationMutation,
} from '../../store/api/portalApi';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: var(--bg-base, #f4f4f5);
`;

const CardWrap = styled(Card)`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Meta = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  background: var(--bg-danger-subtle, #fef2f2);
  border: 1px solid var(--border-danger, #fecaca);
  border-radius: 0.5rem;
  color: var(--text-danger, #dc2626);
  font-size: 0.875rem;
`;

function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

const AcceptInvite: React.FC = () => {
  const { inviteCode = '' } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isInitializing } = useAuth();
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const {
    data: preview,
    isLoading,
    isError,
  } = useGetInvitePreviewQuery(inviteCode, { skip: !inviteCode });

  const [acceptInvitation, { isLoading: accepting }] = useAcceptInvitationMutation();

  const returnTo = `/invite/${encodeURIComponent(inviteCode)}`;
  const authHref = `/auth?returnTo=${encodeURIComponent(returnTo)}`;

  const handleAccept = async () => {
    setAcceptError(null);
    try {
      await acceptInvitation(inviteCode).unwrap();
      navigate('/business', { replace: true });
    } catch {
      setAcceptError('Could not accept this invitation. Please try again or contact support.');
    }
  };

  if (!inviteCode) {
    return (
      <Page>
        <CardWrap padding={24} elevation="sm">
          <Text variant="h3">Invalid invitation link</Text>
          <Meta>This invite link is missing an invitation code.</Meta>
          <Link to="/auth">Go to sign in</Link>
        </CardWrap>
      </Page>
    );
  }

  if (isLoading || isInitializing) {
    return (
      <Page>
        <CardWrap padding={24} elevation="sm">
          <Text variant="body1">Loading invitation...</Text>
        </CardWrap>
      </Page>
    );
  }

  if (isError || !preview) {
    return (
      <Page>
        <CardWrap padding={24} elevation="sm">
          <Text variant="h3">Invitation not found</Text>
          <Meta>
            This invitation link may be invalid, expired, or already used.
          </Meta>
          <Link to="/auth">Go to sign in</Link>
        </CardWrap>
      </Page>
    );
  }

  const isActionable = preview.status === 'pending';

  return (
    <Page>
      <CardWrap padding={24} elevation="sm">
        <Text variant="h3">Organisation invitation</Text>

        {preview.status === 'pending' && (
          <>
            <Meta>
              <strong>{preview.inviterName || 'A team member'}</strong> invited you to join{' '}
              <strong>{preview.orgName || 'an organisation'}</strong>
              {preview.roleName ? ` as ${preview.roleName}` : ''}.
            </Meta>
            {preview.expiresAt && (
              <Meta>Expires {formatDate(preview.expiresAt)}</Meta>
            )}
          </>
        )}

        {preview.status === 'expired' && (
          <Meta>This invitation has expired. Ask the sender to resend it.</Meta>
        )}

        {preview.status === 'accepted' && (
          <Meta>This invitation has already been accepted.</Meta>
        )}

        {preview.status === 'cancelled' && (
          <Meta>This invitation was cancelled by the organisation.</Meta>
        )}

        {preview.status === 'declined' && (
          <Meta>This invitation was declined.</Meta>
        )}

        {acceptError && <ErrorBanner>{acceptError}</ErrorBanner>}

        {isActionable && (
          <Actions>
            {isAuthenticated ? (
              <Button variant="primary" onClick={handleAccept} disabled={accepting}>
                {accepting ? 'Accepting...' : 'Accept invitation'}
              </Button>
            ) : (
              <>
                <Link to={authHref} style={{ textDecoration: 'none' }}>
                  <Button variant="primary" fullWidth>
                    Sign in to accept
                  </Button>
                </Link>
                <Link to={authHref} style={{ textDecoration: 'none' }}>
                  <Button variant="ghost" fullWidth>
                    Create an account
                  </Button>
                </Link>
              </>
            )}
          </Actions>
        )}

        {!isActionable && (
          <Actions>
            <Link to={isAuthenticated ? '/agents' : '/auth'} style={{ textDecoration: 'none' }}>
              <Button variant="ghost" fullWidth>
                {isAuthenticated ? 'Go to dashboard' : 'Go to sign in'}
              </Button>
            </Link>
          </Actions>
        )}
      </CardWrap>
    </Page>
  );
};

export default AcceptInvite;
